import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate, useParams } from "react-router-dom";
import { Pagination } from "antd";
import { GET_USER_NOTIFICATIONS_HISTORY } from "../../graphql/notificationHistoryQueries";
import { MARK_NOTIFICATIONS_READ } from "../../graphql/agentQueries";
import { NotificationsReceivedSkeleton } from "../../components/states";
import NotificationDetailModal from "../../components/NotificationDetailModal/NotificationDetailModal";
import {
  isMarketing,
  getListingPath,
  getStoredRoleName,
} from "../../utils/notificationNav";

const DAYS_BACK = 30;
const POLL_INTERVAL_MS = 30000;
const PAGE_SIZE = 10;

const TYPE_META = {
  AGENT_MATCH: { label: "Agent Match", dot: "bg-primary" },
  BUYER_MATCH: { label: "Buyer Match", dot: "bg-blue" },
  NEW_LISTING: { label: "New Listing", dot: "bg-yellow" },
  LISTING_FAVORITED: { label: "Favorited", dot: "bg-rose-500" },
  ADMIN_BROADCAST: { label: "Broadcast", dot: "bg-secondary" },
};

const isSameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const dayLabel = (date) => {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, now)) return "Today";
  if (isSameDay(date, yesterday)) return "Yesterday";
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const timeLabel = (date) =>
  date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });

// Owns the history query and read-state mutation. Lives at the page level so
// the Received tab badge stays fresh (30s poll) while any tab is active.
export const useNotificationsHistory = () => {
  const { data, loading, refetch } = useQuery(GET_USER_NOTIFICATIONS_HISTORY, {
    variables: { daysBack: DAYS_BACK },
    fetchPolicy: "network-only",
    pollInterval: POLL_INTERVAL_MS,
  });

  const [markAsRead, { loading: marking }] = useMutation(MARK_NOTIFICATIONS_READ, {
    onCompleted: () => refetch(),
  });

  const notifications = useMemo(
    () => data?.getUserNotificationsHistory || [],
    [data]
  );

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const markAllRead = useCallback(() => {
    const unreadIds = notifications.filter((n) => !n.read).map((n) => n._id);
    if (unreadIds.length === 0) return;
    markAsRead({ variables: { notificationIds: unreadIds } });
  }, [notifications, markAsRead]);

  return { notifications, unreadCount, loading, marking, markAsRead, markAllRead };
};

const NotificationsHistory = ({ history }) => {
  const { notifications, unreadCount, loading, marking, markAsRead, markAllRead } =
    history;
  const [selected, setSelected] = useState(null);
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const { companyId } = useParams();

  // The 30s poll can shrink the list; clamp so we never sit on an empty page.
  const safePage = Math.min(
    page,
    Math.max(1, Math.ceil(notifications.length / PAGE_SIZE))
  );

  const visibleNotifications = useMemo(
    () => notifications.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [notifications, safePage]
  );

  const groupedByDay = useMemo(() => {
    const groups = new Map();
    visibleNotifications.forEach((n) => {
      const d = new Date(+n.createdAt);
      if (Number.isNaN(d.getTime())) return;
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!groups.has(key)) {
        groups.set(key, { label: dayLabel(d), items: [] });
      }
      groups.get(key).items.push({ notification: n, date: d });
    });
    return Array.from(groups.values());
  }, [visibleNotifications]);

  const handleOpen = useCallback(
    (notification) => {
      if (!notification.read) {
        markAsRead({ variables: { notificationIds: [notification._id] } });
      }

      // System-generated with a linked listing → navigate (company context only).
      // Marketing (broadcast) or super-admin context → open read-only modal.
      if (
        !isMarketing(notification) &&
        notification.listing?._id &&
        companyId
      ) {
        const roleName = getStoredRoleName();
        navigate(getListingPath(companyId, notification.listing._id, roleName));
        return;
      }

      setSelected(notification);
    },
    [markAsRead, navigate, companyId]
  );

  const handleClose = useCallback(() => setSelected(null), []);

  return (
    <div className="w-full pb-12">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-4">
        <p className="text-sm text-secondary m-0">
          Showing notifications from the last {DAYS_BACK} days.
        </p>
        <button
          type="button"
          onClick={markAllRead}
          disabled={unreadCount === 0 || marking}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold ${
            unreadCount === 0 || marking
              ? "bg-gray-100 text-gray-400 cursor-not-allowed"
              : "bg-primary text-white hover:bg-primary_dark"
          }`}
        >
          Mark all read
        </button>
      </div>

      {loading && !notifications.length ? (
        <NotificationsReceivedSkeleton />
      ) : notifications.length === 0 ? (
        <EmptyState />
      ) : (
        <>
          <div className="flex flex-col gap-8">
            {groupedByDay.map((group) => (
              <section key={group.label}>
                <h2 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">
                  {group.label}
                </h2>
                <div className="bg-white rounded-2xl shadow-sm divide-y divide-gray-100 overflow-hidden">
                  {group.items.map(({ notification, date }) => (
                    <NotificationRow
                      key={notification._id}
                      notification={notification}
                      date={date}
                      onOpen={handleOpen}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>
          <div className="mt-6 flex justify-center">
            <Pagination
              current={safePage}
              pageSize={PAGE_SIZE}
              total={notifications.length}
              onChange={setPage}
              showSizeChanger={false}
              hideOnSinglePage
            />
          </div>
        </>
      )}

      {selected && (
        <NotificationDetailModal
          notification={selected}
          onClose={handleClose}
        />
      )}
    </div>
  );
};

const NotificationRow = ({ notification, date, onOpen }) => {
  const handleClick = useCallback(() => onOpen(notification), [onOpen, notification]);
  const meta = TYPE_META[notification.type] || {
    label: notification.type || "Notification",
    dot: "bg-gray-400",
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      className={`w-full flex items-start gap-4 text-left px-5 py-4 hover:bg-gray-50 transition-colors ${
        notification.read ? "" : "bg-primary/5"
      }`}
    >
      <span
        className={`mt-2 w-2 h-2 rounded-full flex-shrink-0 ${
          notification.read ? "bg-transparent" : "bg-primary"
        }`}
        aria-hidden="true"
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span
            className={`inline-block w-1.5 h-1.5 rounded-full ${meta.dot}`}
            aria-hidden="true"
          />
          <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
            {meta.label}
          </span>
        </div>
        <div className="text-sm font-semibold text-gray-900 truncate">
          {notification.title || "Notification"}
        </div>
        <div className="text-sm text-secondary line-clamp-2 mt-1">
          {notification.body || ""}
        </div>
      </div>
      <span className="text-xs text-gray-400 flex-shrink-0 mt-1">
        {timeLabel(date)}
      </span>
    </button>
  );
};

const EmptyState = () => (
  <div className="bg-white rounded-2xl shadow-sm p-12 text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 text-gray-400 mb-4">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    </div>
    <h3 className="text-base font-semibold text-gray-700 m-0">No notifications</h3>
    <p className="text-sm text-gray-500 mt-1 m-0">
      You'll see notifications from the last {DAYS_BACK} days here.
    </p>
  </div>
);

export default NotificationsHistory;
